<template>
    <div>
        <b-overlay :show="loading.sites_loading" :variant="'light'" :opacity="0.85" :blur="'2px'" rounded="sm">
            <b-card class="mb-2">
                <div class="float-left">
                    <h2>Your Sites</h2>
                </div>
                <div class="float-right">
                    <b-button class="mr-1" v-b-tooltip.hover title="Open the DocPusher how-to guide" size="md" variant="light" alt="Refresh page data" target="_blank" href="https://docs.google.com/document/d/1UH0L4P0Irse_l71CGg9QOzsZYo1jUWxRyZA3Dw3nJdI/preview?usp=sharing">
                        <i class="fas fa-question-circle"></i> Help
                    </b-button>
                    <b-button v-b-tooltip.hover title="Refresh the status of your DocPusher sites." size="md" variant="light" alt="Refresh page data" v-on:click="reload_sites_data">
                        <i class="fas fa-sync-alt"></i> {{loading.time_to_auto_refresh}}
                    </b-button>
                </div>
                <br />
                <hr class="mt-4" />
                <b-alert show v-if="sites.length > 0">
                    <i class="fas fa-info-circle"></i> Your site's Google Drive folder will be scanned for changes every 10 seconds and the modified docs that are ready for publishing will appear below. To immediately check if DocPusher has pulled your changes, click the refresh button in the top right corner.
                </b-alert>
                <b-card v-for="site in sites" v-bind:key="site.metadata.site_id" class="mb-3">
                    <template v-slot:header>
                        <h6 class="mb-0">
                            <i class="fas fa-globe-americas"></i>
                            &nbsp;Website URL:
                            <a v-bind:href="'http://' + site.metadata.subdomain + '.docpusher.com'" target="_blank">
                                <code>http://{{site.metadata.subdomain}}.docpusher.com</code>
                            </a>
                        </h6>
                    </template>
                    <b-card-text v-if="site.docs.length > 0">
                        <h3 class="mb-4">Updated docs not yet published to your site: </h3>
                        <table role="table" aria-busy="false" aria-colcount="9" class="table b-table table-striped table-hover">
                            <thead role="rowgroup">
                                <tr role="row">
                                    <th role="columnheader" scope="col">
                                        <div>Name</div>
                                    </th>
                                    <th role="columnheader" scope="col" style="width: 50px">
                                        <div>Type</div>
                                    </th>
                                    <th role="columnheader" scope="col" style="width: 150px;">
                                        <div>Last Updated</div>
                                    </th>
                                    <th role="columnheader" scope="col" style="width: 100px">
                                        <div>View</div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody role="rowgroup">
                                <tr role="row" class="" v-for="doc in site.docs" v-bind:key="doc.file_id">
                                    <td role="cell">
                                        <b-badge pill variant="danger" v-if="doc.deleted">
                                            <i class="fas fa-trash-alt"></i> Queued for Deletion
                                        </b-badge> {{doc.name}}
                                    </td>
                                    <td role="cell">{{doc.doc_type.toUpperCase()}}</td>
                                    <td role="cell">{{doc.timestamp | moment("from")}}</td>
                                    <td role="cell">
                                        <b-button v-bind:href="'https://docs.google.com/document/d/' + doc.file_id" variant="primary" target="_blank" size="sm" v-if="!doc.deleted">
                                            <i class="fas fa-file-alt"></i> Open
                                        </b-button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </b-card-text>
                    <b-card-text v-if="site.docs.length === 0">
                        You have not updated any Google Docs related to this site recently. Once you add or edit a Google Doc in the site's Google Drive folder, you will be able to update your site with the changes.
                    </b-card-text>
                    <div>
                        <b-button v-b-tooltip.hover title="Publish all doc updates to your website." class="mr-1" variant="primary" v-if="site.docs.length > 0" v-on:click="start_site_build(site.metadata.site_id)">
                            <i class="fas fa-play-circle"></i> Update from Docs
                        </b-button>
                        <b-button v-b-tooltip.hover title="Open Google Drive folder for this website." class="mr-1" variant="primary" target="_blank" v-bind:href="'https://drive.google.com/drive/folders/' + site.metadata.webroot_folder_id">
                            <i class="fas fa-external-link-alt"></i> Open Drive Folder
                        </b-button>
                        <b-button v-b-tooltip.hover title="Modify site settings like title, author, social media links, etc." v-b-modal.edit-site-config-yaml class="mr-1" variant="primary" v-on:click="set_selected_site(site.metadata.site_id)">
                            <i class="fas fa-cogs"></i> Settings
                        </b-button>
                    </div>
                    <div v-if="site.build_statuses && site.build_statuses.length > 0">
                        <div v-for="build_status in site.build_statuses" v-bind:key="build_status.id">
                            <b-alert show variant="warning" class="mt-2 mb-2" v-if="build_status.build_status === 'IN_PROGRESS'">
                                <div>
                                    <i class="fas fa-cogs"></i> Build ID <code>{{build_status.id}}</code> is in progress, <span v-if="build_status.stats.in_progress > 0">{{build_status.stats.in_progress}} Google Doc conversions remaining...</span><span v-if="build_status.stats.in_progress === 0">all Google Docs have been converted. The final site publishing step is in progress...</span>
                                </div>
                                <b-progress :value="build_status.stats.percent_complete" variant="warning" striped animated class="mt-2"></b-progress>
                            </b-alert>
                            <b-alert show variant="success" class="mt-2 mb-2" v-if="build_status.build_status === 'COMPLETE'">
                                <div style="width: 100%; height: 25px;">
                                    <div class="float-left">
                                        <i class="fas fa-cogs"></i> Build ID <code>{{build_status.id}}</code> completed successfully.
                                    </div>
                                </div>
                            </b-alert>
                        </div>
                    </div>
                </b-card>
                <hr />
                <div class="float-right" v-if="site_count < 5 || user_type == 'UNLIMITED'">
                    <b-button v-b-tooltip.hover title="Create a new DocPusher website" size="lg" variant="primary" v-on:click="navigate_to_new_site">
                        <i class="fas fa-plus"></i> Create New Site
                    </b-button>
                </div>
                <div class="float-left" v-b-tooltip.hover title="Use your own domain names with DocPusher.">
                    <b-button size="lg" variant="primary" v-on:click="show_manage_domains_modal">
                        <i class="fas fa-atlas"></i> Manage Domains
                    </b-button>
                </div>
                <div class="float-right" v-if="site_count >= 5 && user_type == 'FREE'" v-b-tooltip.hover title="You already have the maximum number of sites allowed for your free account.">
                    <b-button size="lg" variant="primary" disabled>
                        <i class="fas fa-plus"></i> Create New Site
                    </b-button>
                </div>
            </b-card>
        </b-overlay>
        <!-- Site YAML config editor -->
        <b-modal id="edit-site-config-yaml" size="lg" title="Edit Site Config" hide-footer>
            <b-overlay :show="loading.save_config_loading" :variant="'light'" :opacity="0.85" :blur="'2px'" rounded="sm">
                <codemirror ref="cmEditor" v-model="current_site_config.config_yaml" :options="cmOptions" @ready="editor_ready" @input="editor_changed" />
                <hr />
                <div v-if="!current_site_config.is_valid">
                    <b-alert show variant="danger">
                        <i class="fas fa-exclamation-circle"></i> YAML error, please fix before you can save the site config: <code>{{current_site_config.yaml_error}}</code>
                    </b-alert>
                    <hr />
                </div>
                <b-overlay :show="loading.custom_domains_loading || loading.recheck_records_loading" :variant="'light'" :opacity="0.85" :blur="'2px'" rounded="sm">
                    <div v-if="domains_pointed_to_current_site.length > 0">
                        <div v-for="domain_data in domains_pointed_to_current_site" v-bind:key="domain_data.domain_id">
                            <!-- If the domain is not set up fully, show the prompt to 
                            tell the user to point their domain to the cloudfront.net
                            custom subdomain via CNAME. -->
                            <div v-if="domain_data.status === 'DOMAIN_NOT_SETUP'">
                                <b-alert show variant="warning">
                                    <div style="text-align: center;" class="mb-2">
                                        <i class="fas fa-info-circle"></i> To finish setting up your custom domain, create a <code>CNAME</code> record for <code>{{domain_data.hostname}}</code> pointing it to <code>{{domain_data.cloudfront_host}}</code>. Once you've done so, click the "Recheck Domain" button.
                                    </div>
                                    <div style="text-align: right">
                                        <b-button variant="primary" pill size="sm" v-on:click="recheck_domain_records(domain_data.domain_id)" class="mr-2">
                                            <i class="fas fa-sync-alt"></i> Recheck Domain
                                        </b-button>
                                        <b-button variant="danger" pill size="sm" v-on:click="dissociate_domain(domain_data.domain_id)">
                                            <i class="fas fa-unlink"></i> Unlink Domain from Site
                                        </b-button>
                                    </div>
                                </b-alert>
                            </div>
                            <!-- -->
                            <div v-if="domain_data.status === 'IN_USE'">
                                <b-alert show variant="success">
                                    <div style="text-align: center;" class="mb-2">
                                        <i class="fas fa-info-circle"></i> Your custom domain <code>{{domain_data.hostname}}</code> is set up properly and is pointing to this site. <a v-bind:href="'https://' + domain_data.hostname" target="_blank">Click here to view the site.</a>
                                    </div>
                                    <div style="text-align: right">
                                        <b-button variant="danger" pill size="sm" v-on:click="dissociate_domain(domain_data.domain_id)">
                                            <i class="fas fa-unlink"></i> Unlink Domain from Site
                                        </b-button>
                                    </div>
                                </b-alert>
                            </div>
                        </div>
                        <hr />
                    </div>
                </b-overlay>
                <div v-if="unallocated_custom_domains_options.length > 1">
                    <b-overlay :show="loading.custom_domain_dropdown" :variant="'light'" :opacity="0.85" :blur="'2px'" rounded="sm">
                        <div class="mb-2 d-flex">
                            <!-- List of unallocated custom domains  unallocated_custom_domains-->
                            <b-form-select v-model="custom_domain.selected_unallocated_custom_domain" :options="unallocated_custom_domains_options" class="mr-2"></b-form-select>
                            <b-button variant="primary" style="width: 275px" v-b-tooltip.hover title="Point this custom domain to this DocPusher website." v-on:click="associate_domain(current_site_config.site_id)" v-bind:disabled="!custom_domain.selected_unallocated_custom_domain">
                                <i class="fas fa-save"></i> Link Domain to Site
                            </b-button>
                        </div>
                        <hr />
                    </b-overlay>
                </div>
                <b-button v-b-tooltip.hover title="Manually kick off a site build with the latest config." class="mb-2" block variant="primary" v-on:click="manual_site_build(current_site_config.site_id)" v-bind:disabled="!current_site_config.is_valid">
                    <i class="fas fa-cogs"></i> Start Manual Site Build
                </b-button>
                <b-button v-b-tooltip.hover title="Save the current site config. This will not take effect until you start a site build." block variant="success" v-on:click="save_site_config" v-bind:disabled="!current_site_config.is_valid">
                    <i class="fas fa-save"></i> Save Site Config
                </b-button>
            </b-overlay>
        </b-modal>
        <!-- Manage domains -->
        <b-modal id="manage-domains" size="lg" title="Manage Custom Domains" hide-footer>
            <b-overlay :show="loading.custom_domains_loading" :variant="'light'" :opacity="0.85" :blur="'2px'" rounded="sm">
                <b-card v-for="domain_data in custom_domain.domains" v-bind:key="domain_data.domain_id">
                    <!-- Domain -->
                    <div style="display: inline">
                        <b>Hostname: </b><code>{{domain_data.hostname}}</code>
                    </div>
                    <!-- Status -->
                    <b-badge pill variant="warning" v-if="domain_data.status === 'CERT_VALIDATION_REQUIRED'" class="ml-2">
                        <i class="fas fa-exclamation-triangle"></i> DNS verification required.
                    </b-badge>
                    <b-badge pill variant="info" v-if="domain_data.status === 'DOMAIN_NOT_SETUP'" class="ml-2">
                        <i class="far fa-check-circle"></i> Domain verified but not pointing to any of your sites.
                    </b-badge>
                    <b-badge pill variant="success" v-if="domain_data.status === 'IN_USE'" class="ml-2">
                        <i class="far fa-check-circle"></i> Domain verified and in use.
                    </b-badge>
                    <b-badge pill variant="danger" v-if="domain_data.status === 'FAILED'" class="ml-2">
                        <i class="fas fa-exclamation-circle"></i> Failed
                    </b-badge>
                    <!-- Info -->
                    <br />
                    <span v-if="domain_data.status === 'FAILED'" class="mt-2">
                        Domain verification has failed, please delete this domain and try again.
                    </span>
                    <span v-if="domain_data.status === 'CERT_VALIDATION_REQUIRED'" class="mt-2">
                        <span>
                            Before this domain can be used, please verify your domain name by creating the following records:
                        </span>
                        <b-card class="mt-2 mb-2">
                            <span v-for="(dns_record, index) in domain_data.cert_verification_dns_records" v-bind:key="dns_record.name">
                                <b>Status</b>: <b-badge variant="danger" v-if="dns_record.status === 'PENDING_VALIDATION'">
                                    Not Found
                                </b-badge>
                                <b-badge variant="success" v-if="dns_record.status === 'SUCCESS'">
                                    Validated
                                </b-badge>
                                <br />
                                <b>Type</b>: <code>{{dns_record.type}}</code><br />
                                <b>Name</b>: <code>{{dns_record.name}}</code><br />
                                <b>Value</b>: <code>{{dns_record.value}}</code><br />
                                <hr v-if="index < (domain_data.cert_verification_dns_records.length - 1)" />
                            </span>
                        </b-card>
                        <p>When you've created the above records, click the button below to recheck your records. Note that there may be a slight delay before the changes are recognized.</p>
                        <b-overlay :show="loading.recheck_records_loading" :variant="'light'" :opacity="0.85" :blur="'2px'" rounded="sm">
                            <b-button variant="primary" v-on:click="recheck_domain_records(domain_data.domain_id)">
                                <i class="fas fa-sync-alt"></i> Recheck Records
                            </b-button>
                        </b-overlay>
                    </span>
                    <span v-if="domain_data.status === 'DOMAIN_NOT_SETUP'" class="info-column">
                        To use this domain, click the <code>Settings</code> button for one of your sites and assign this domain to it.
                    </span>
                    <span v-if="domain_data.status === 'IN_USE'" class="info-column">
                        Domain verified and in use.
                    </span>
                    <br />
                    <div class="float-right mt-2">
                        <b-button variant="danger" pill size="sm" v-on:click="delete_domain(domain_data.domain_id)">
                            <i class="fas fa-trash-alt"></i> Delete Domain
                        </b-button>
                    </div>
                </b-card>
                <b-overlay :show="loading.add_custom_domain_loading" :variant="'light'" :opacity="0.85" :blur="'2px'" rounded="sm">
                    <b-card title="Add External Domain" class="mb-2">
                        <b-card-text>
                            Add your own custom domain names (<code>example.com</code>, <code>subdomain.example.com</code>) and use them for your DocPusher websites.
                        </b-card-text>
                        <div>
                            <b-input-group prepend="Domain to import" class="mb-2 mr-sm-2 mb-sm-0">
                                <b-input placeholder="example.com" v-model="custom_domain.new_domain" :state="is_custom_domain_valid" autofocus></b-input>
                                <span class="input-group-addon" style="margin-left: 5px">
                                    <b-button variant="primary" :disabled="!is_custom_domain_valid" v-on:click="add_domain">
                                        <i class="fas fa-file-import"></i> Import
                                    </b-button>
                                </span>
                            </b-input-group>
                            <b-form-invalid-feedback :state="is_custom_domain_valid">
                                Domain name not valid.
                            </b-form-invalid-feedback>
                        </div>
                    </b-card>
                </b-overlay>
            </b-overlay>
        </b-modal>
    </div>
</template>
<script>
//import Vue from 'vue';
import api from '../libs/api.js';
import $ from 'jquery'
const psl = require('psl');
const yaml = require('js-yaml');

export default {
    name: 'ViewSites',
    props: {},
    computed: {
        is_custom_domain_valid() {
            if (this.custom_domain.new_domain === "") {
                return null;
            }

            if (!psl.isValid(this.custom_domain.new_domain)) {
                return false;
            }

            return true;
        },
        unallocated_custom_domains() {
            return this.custom_domain.domains.filter(function(custom_domain_data) {
                return (custom_domain_data.status === "DOMAIN_NOT_SETUP" && !custom_domain_data.cloudfront_host);
            });
        },
        unallocated_custom_domains_options() {
            var return_options = this.unallocated_custom_domains.map(custom_domain_data => {
                return {
                    text: custom_domain_data.hostname,
                    value: custom_domain_data.domain_id
                }
            });

            return_options = return_options.concat([
                { value: null, text: '-- Select an unallocated custom domain --' }
            ]);

            return return_options;
        },
        domains_pointed_to_current_site() {
            const current_site_id = this.current_site_config.site_id;
            return this.custom_domain.domains.filter(domain_data => {
                return domain_data.associated_site_id === current_site_id;
            });
        }
    },
    data() {
        return {
            'user': {},
            'custom_domain': {
                'new_domain': '',
                'domains': [],
                'selected_unallocated_custom_domain': null,
            },
            'current_site_config': {
                'site_id': null,
                'config_yaml': '',
                'is_valid': true,
                'yaml_error': '',
            },
            'loading': {
                'custom_domain_dropdown': false,
                'recheck_records_loading': false,
                'add_custom_domain_loading': false,
                'custom_domains_loading': false,
                'sites_loading': false,
                'save_config_loading': false,
                'last_reload_time': 0,
                'time_to_auto_refresh': 60,
            },
            'sites': [],
            'site_count': 0,
            'user_type': 'FREE',
            cmOptions: {
                tabSize: 2,
                mode: 'text/x-yaml',
                theme: 'monokai',
                lineNumbers: true,
                line: true,
                lint: true
            },
        }
    },
    // On page load
    async mounted() {
        // dev
        window.app = this;

        await Promise.all([
            this.reload_sites_data()
        ]);

        // Pull user profile data

        // If the user has no sites created then
        // redirect them to the create new site page.
        if (this.sites.length === 0) {
            this.$router.push('/new_site');
            return
        }

        this.loading.last_reload_time = get_unix_timestamp();

        // The master refresh which reloads the status of all user sites
        clearInterval(window.refresh_interval);
        window.refresh_interval = setInterval(() => {
            const time_to_next_update = this.get_time_to_next_full_update();

            if (time_to_next_update <= 0) {
                this.loading.time_to_auto_refresh = 0;
                this.reload_sites_data();
                return
            }

            this.loading.time_to_auto_refresh = time_to_next_update;
        }, (1000));

        // The active builds quick-reload refresh. This only applies if
        // the user has an active build and will refresh their build status
        // at a much faster interval.
        clearInterval(window.active_builds_refresh);
        window.active_builds_refresh = setInterval(() => {
            this.reload_active_builds();
        }, (1000 * 6));
    },
    // On unmount
    destroyed() {
        clearInterval(window.refresh_interval);
    },
    methods: {
        async dissociate_domain(domain_id) {
            this.loading.custom_domains_loading = true;
            await api.dissociate_domain(
                domain_id
            );
            this.loading.custom_domains_loading = false;
            await this.refresh_domain_list();
        },
        async associate_domain(site_id) {
            const domain_id = this.custom_domain.selected_unallocated_custom_domain;
            this.loading.custom_domain_dropdown = true;
            const associate_result = await api.associate_domain_with_site(
                domain_id,
                site_id
            );

            if (!associate_result.association_successful) {
                // TODO: Toastr with error details.
                this.$toastr.e(
                    'Linking the domain to this site failed. Please delete your previous hostname CNAME record pointing to <b>cloudfront.net</b> before continuing.',
                    'Domain Link Failed',
                );
            }

            this.loading.custom_domain_dropdown = false;
            await this.refresh_domain_list();
        },
        async delete_domain(domain_id) {
            this.loading.custom_domains_loading = true;
            await api.delete_domain(
                domain_id
            );
            await this.refresh_domain_list();
            this.loading.custom_domains_loading = false;
            await this.refresh_domain_list();
        },
        async recheck_domain_records(domain_id) {
            this.loading.recheck_records_loading = true;
            await api.recheck_domain(
                domain_id
            );
            this.loading.recheck_records_loading = false;
            await this.refresh_domain_list();
        },
        async refresh_domain_list() {
            this.loading.custom_domains_loading = true;
            const domains_result = await api.get_domains();
            this.loading.custom_domains_loading = false;
            this.custom_domain.domains = domains_result.domains;
        },
        async add_domain() {
            this.loading.add_custom_domain_loading = true;
            await api.add_domain(
                this.custom_domain.new_domain
            );
            this.loading.add_custom_domain_loading = false;

            this.custom_domain.new_domain = '';

            // Refresh the domain list so it includes the new one
            await this.refresh_domain_list();
        },
        async show_manage_domains_modal() {
            this.$bvModal.show('manage-domains');
            await this.refresh_domain_list();
            $("#manage-domains").scrollTop(0);
        },
        get_time_to_next_full_update: function() {
            const current_timestamp = get_unix_timestamp();
            const time_since_last_update = current_timestamp - this.loading.last_reload_time;
            const time_to_next_update = 60 - time_since_last_update;
            return time_to_next_update;
        },
        manual_site_build: async function(site_id) {
            await this.save_site_config();
            await this.start_site_build(site_id);
        },
        save_site_config: async function() {
            this.loading.save_config_loading = true;
            const result = await api.set_site_config(
                this.current_site_config.site_id,
                this.current_site_config.config_yaml
            );
            this.loading.save_config_loading = false;
            this.$bvModal.hide('edit-site-config-yaml');

            this.sites = this.sites.map(site_data => {
                if (site_data.metadata.site_id === this.current_site_config.site_id) {
                    site_data.metadata.human_config = result.config_yaml;
                }
                return site_data;
            });
            this.current_site_config.site_id = null;
            this.current_site_config.config_yaml = '';
            this.current_site_config.is_valid = true;
            this.current_site_config.yaml_error = '';
        },
        set_selected_site: function(site_id) {
            this.custom_domain.selected_unallocated_custom_domain = null;
            this.refresh_domain_list();
            this.current_site_config.site_id = site_id;
            const matching_site_data = this.sites.filter(site_data => {
                return site_data.metadata.site_id === site_id;
            });
            this.current_site_config.config_yaml = matching_site_data[0].metadata.human_config;
        },
        editor_ready: function(editor) {
            this.current_site_config.is_valid = true;
            this.current_site_config.yaml_error = '';
            const editor_hack = () => {
                editor.refresh();
                editor.focus();
            };
            for (let i = 0; i < 20; i++) {
                setTimeout(editor_hack, (i * 100));
            }
        },
        editor_changed: function(new_content) {
            // Attempt to parse yaml.
            try {
                yaml.safeLoad(new_content);
                this.current_site_config.is_valid = true;
                this.current_site_config.yaml_error = '';
            } catch (e) {
                console.error(e);
                this.current_site_config.is_valid = false;
                this.current_site_config.yaml_error = e.message.toString();
            }
        },
        start_site_build: async function(site_id) {
            this.$bvToast.toast('Your site is now being updated, please wait...', {
                title: 'Update Started',
                variant: 'success',
                solid: false
            });
            await api.start_site_build(site_id);
            await this.reload_sites_data();
        },
        reload_active_builds: async function() {
            // If there is an active build running then it will
            // be refreshed every 5 seconds until it's finished.

            // The refresh will be skipped if the master refresh
            // is happening in >10 seconds.
            const time_to_next_update = this.get_time_to_next_full_update();
            if (time_to_next_update <= 10) {
                console.log(`Time to next full refresh is ${time_to_next_update} which is <= 10 seconds. Skipping the build-level refresh.`)
                return
            }

            const current_timestamp = get_unix_timestamp();

            const sites_to_refresh = this.sites.filter(site_data => {
                var should_refresh_site = false;

                const time_since_last_update = current_timestamp - site_data.last_refresh;
                const builds_in_progress = site_data.build_statuses.filter(build_status => {
                    return (build_status.build_status === 'IN_PROGRESS');
                });

                // If it's been more than five seconds since last
                // site build refresh AND there are builds in progress
                // then the site data can be reloaded.
                if (time_since_last_update >= 5 && builds_in_progress.length > 0) {
                    should_refresh_site = true;
                }

                return should_refresh_site;
            });

            const site_ids_to_refresh = sites_to_refresh.map(site_data => {
                return site_data.id;
            });

            if (site_ids_to_refresh.length === 0) {
                //console.log(`No active builds which need to be quick-refreshed.`);
                return
            }

            await this.reload_site_ids(site_ids_to_refresh);
        },
        reload_site_ids: async function(site_ids) {
            // Pull build info for all specified site_ids
            // Hold and resolve after grabbing info for all sites
            const build_statuses_promise = site_ids.map(async site_id => {
                const build_status = await api.get_site_builds(
                    site_id
                );

                return {
                    'site_id': site_id,
                    'build_status': build_status
                };
            });

            var new_sites_data = [];

            await Promise.all(site_ids.map(async site_id => {
                const site_data = await api.get_changed_docs_for_site(site_id);
                this.site_count = site_data.site_count;
                this.user_type = site_data.user_type;
                new_sites_data.push(site_data);
            }));

            // Now resolve the build statuses
            const build_statuses = await Promise.all(build_statuses_promise);

            // Enrich the sites data with build statuses
            build_statuses.map(build_status => {
                const site_id = build_status.site_id;

                new_sites_data = new_sites_data.map(site_data => {
                    // Check if it's a match to our site
                    // If it is then add the build data
                    if (site_data.metadata.site_id !== site_id) {
                        return site_data;
                    }
                    site_data.build_statuses = build_status.build_status.build_statuses;

                    // Add site_id as well
                    site_data.id = site_id;

                    // Add last refresh time
                    site_data.last_refresh = get_unix_timestamp();

                    return site_data;
                });
            });

            // For each site update data, check if we already have
            // it in this.sites. If we do then we'll replace it, otherwise
            // we'll just append it.
            new_sites_data.map(site_data => {
                var is_in_sites_already = false;

                this.sites.map((existing_site_data, index) => {
                    // If it is in this.sites we'll just update that record
                    // in the array directly.
                    if (site_data.id === existing_site_data.id) {
                        //console.log(`Site ID ${site_data.id} is already in existing sites! Updating entry...`)
                        is_in_sites_already = true;

                        // Update it directly.
                        this.sites[index] = copy(site_data);
                    }
                });

                // If it wasn't in this.sites we'll just append it to this.sites
                if (!is_in_sites_already) {
                    //console.log(`Site ID ${site_data.id} isn't in this.sites, appending it...`);
                    this.sites.push(site_data);
                }
            });

            // Sort the sites to be in deterministic order.
            // Custom sort function for alphabetical name
            function subdomain_sort_func(a, b) {
                if (a.metadata.subdomain < b.metadata.subdomain) {
                    return -1;
                }
                if (a.metadata.subdomain > b.metadata.subdomain) {
                    return 1;
                }
                return 0;
            }
            this.sites.sort(subdomain_sort_func);
        },
        reload_sites_data: async function() {
            this.loading.last_reload_time = get_unix_timestamp();

            // Clear previous sites
            this.sites = [];

            // Pull site IDs
            this.loading.sites_loading = true;
            const site_ids = await api.get_user_site_ids();

            await this.reload_site_ids(site_ids);

            this.loading.sites_loading = false;
        },
        navigate_to_new_site: function() {
            this.$router.push('/new_site');
        },

    }
}

function copy(input_data) {
    return JSON.parse(
        JSON.stringify(
            input_data
        )
    );
}

function get_unix_timestamp() {
    return Math.round((new Date()).getTime() / 1000);
}
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.status-column {
    width: 175px;
    max-width: 175px;
}

.domain-column {
    width: 200px;
    max-width: 200px;
}

.info-column {
    max-width: 100%;
}

.domain-table {
    table-layout: fixed;
    word-wrap: break-word;
}
</style>